import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PromoBar from "../components/PromoBar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getPage } from "../lib/api";
import { useCMS } from "../context/CMSContext";

// Very small markdown-ish renderer (headings, lists, blockquote, links, bold, paragraphs)
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split(/\r?\n/);
  const blocks = [];
  let buf = [];
  let inList = null; // 'ul' | 'ol' | null
  let inQuote = false;

  const flushBuf = () => {
    if (buf.length === 0) return;
    blocks.push({ type: "p", text: buf.join(" ") });
    buf = [];
  };
  const closeList = () => {
    if (inList) {
      inList = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line === "") {
      flushBuf();
      closeList();
      inQuote = false;
      continue;
    }
    if (line.startsWith("### ")) {
      flushBuf(); closeList(); inQuote = false;
      blocks.push({ type: "h3", text: line.slice(4) });
      continue;
    }
    if (line.startsWith("## ")) {
      flushBuf(); closeList(); inQuote = false;
      blocks.push({ type: "h2", text: line.slice(3) });
      continue;
    }
    if (line.startsWith("# ")) {
      flushBuf(); closeList(); inQuote = false;
      blocks.push({ type: "h1", text: line.slice(2) });
      continue;
    }
    if (line.startsWith("> ")) {
      flushBuf(); closeList();
      if (!inQuote) {
        blocks.push({ type: "quote", text: line.slice(2) });
        inQuote = true;
      } else {
        const last = blocks[blocks.length - 1];
        last.text += "\n" + line.slice(2);
      }
      continue;
    }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      flushBuf(); inQuote = false;
      const item = line.slice(2);
      if (inList !== "ul") {
        inList = "ul";
        blocks.push({ type: "ul", items: [item] });
      } else {
        blocks[blocks.length - 1].items.push(item);
      }
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      flushBuf(); inQuote = false;
      const item = line.replace(/^\d+\.\s/, "");
      if (inList !== "ol") {
        inList = "ol";
        blocks.push({ type: "ol", items: [item] });
      } else {
        blocks[blocks.length - 1].items.push(item);
      }
      continue;
    }
    closeList(); inQuote = false;
    buf.push(line);
  }
  flushBuf();

  const inline = (text) => {
    // **bold**, [text](url)
    const nodes = [];
    let rest = text;
    let key = 0;
    while (rest.length) {
      const linkMatch = rest.match(/\[([^\]]+)\]\(([^)]+)\)/);
      const boldMatch = rest.match(/\*\*([^*]+)\*\*/);
      const candidates = [linkMatch, boldMatch].filter(Boolean);
      if (candidates.length === 0) {
        nodes.push(<React.Fragment key={key++}>{rest}</React.Fragment>);
        break;
      }
      candidates.sort((a, b) => a.index - b.index);
      const m = candidates[0];
      if (m.index > 0) {
        nodes.push(<React.Fragment key={key++}>{rest.slice(0, m.index)}</React.Fragment>);
      }
      if (m === linkMatch) {
        const isInternal = m[2].startsWith("/") || m[2].startsWith("#");
        if (isInternal) {
          nodes.push(<Link key={key++} to={m[2]} className="text-[#FF6B3D] font-semibold hover:underline">{m[1]}</Link>);
        } else {
          nodes.push(<a key={key++} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-[#FF6B3D] font-semibold hover:underline">{m[1]}</a>);
        }
      } else {
        nodes.push(<strong key={key++} className="font-bold text-[#11151C]">{m[1]}</strong>);
      }
      rest = rest.slice(m.index + m[0].length);
    }
    return nodes;
  };

  return blocks.map((b, i) => {
    // Render-only blocks never reorder, but composite key makes ESLint happy
    // and protects against partial array updates if the CMS later supports
    // inline block insertion.
    const k = `${b.type}-${i}-${(b.text || (b.items || []).join("|")).slice(0, 32)}`;
    if (b.type === "h1") return <h1 key={k} className="lm-font font-extrabold text-3xl sm:text-4xl text-[#11151C] mt-10 mb-4">{inline(b.text)}</h1>;
    if (b.type === "h2") return <h2 key={k} className="lm-font font-extrabold text-2xl sm:text-3xl text-[#11151C] mt-10 mb-3">{inline(b.text)}</h2>;
    if (b.type === "h3") return <h3 key={k} className="lm-font font-bold text-xl text-[#11151C] mt-6 mb-2">{inline(b.text)}</h3>;
    if (b.type === "quote") return (
      <blockquote key={k} className="my-4 border-l-4 border-[#FF6B3D] bg-[#FFFBF6] p-4 rounded-r-lg text-[#11151C]/80">
        {b.text.split("\n").map((l, j) => <div key={`${k}-l${j}-${l.slice(0, 16)}`}>{inline(l)}</div>)}
      </blockquote>
    );
    if (b.type === "ul") return (
      <ul key={k} className="my-4 space-y-1.5 list-disc pl-6 text-[#11151C]/80">
        {b.items.map((it, j) => <li key={`${k}-i${j}-${(it || "").slice(0, 16)}`}>{inline(it)}</li>)}
      </ul>
    );
    if (b.type === "ol") return (
      <ol key={k} className="my-4 space-y-1.5 list-decimal pl-6 text-[#11151C]/80">
        {b.items.map((it, j) => <li key={`${k}-i${j}-${(it || "").slice(0, 16)}`}>{inline(it)}</li>)}
      </ol>
    );
    return <p key={k} className="my-3 text-[#11151C]/80 leading-relaxed">{inline(b.text)}</p>;
  });
}

export default function PagePage() {
  const { slug } = useParams();
  const { cms } = useCMS();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    getPage(slug)
      .then((p) => { setPage(p); setErr(null); })
      .catch(() => setErr("Page not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (page) {
      document.title = page.meta_title || page.title;
      const meta = document.querySelector('meta[name="description"]') || (() => {
        const m = document.createElement("meta");
        m.name = "description";
        document.head.appendChild(m);
        return m;
      })();
      if (page.meta_description) meta.setAttribute("content", page.meta_description);
    }
  }, [page]);

  return (
    <div className="lm-font min-h-screen bg-white text-[#11151C]">
      <PromoBar />
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#11151C]/60 hover:text-[#FF6B3D]">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        {loading && <div className="mt-10 text-[#11151C]/50">Loading...</div>}

        {err && (
          <div className="mt-10">
            <h1 className="lm-font font-extrabold text-3xl text-[#11151C]">Page not found</h1>
            <p className="mt-3 text-[#11151C]/65">The page you're looking for doesn't exist or has been removed.</p>
          </div>
        )}

        {page && (
          <>
            <h1 className="lm-font font-extrabold text-3xl sm:text-5xl text-[#11151C] mt-6 leading-tight">{page.title}</h1>
            <div className="mt-8">{renderMarkdown(page.content)}</div>
          </>
        )}
      </article>
      <Footer />
    </div>
  );
}
